import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SupplierFilters {
  criticality?: string;
  category?: string;
}

interface CreateSupplierData {
  name: string;
  category?: string;
  criticality?: string;
  notes?: string;
  last_assessment?: string;
  next_review?: string;
  questionnaire_score?: number;
  issues_count?: number;
  owner_id?: string;
}

interface UpdateSupplierData {
  name?: string;
  category?: string;
  criticality?: string;
  notes?: string;
  last_assessment?: string;
  next_review?: string;
  questionnaire_score?: number;
  issues_count?: number;
  owner_id?: string | null;
}

export const useSuppliers = (orgId?: string, filters?: SupplierFilters) => {
  const queryClient = useQueryClient();

  const suppliersQuery = useQuery({
    queryKey: ['suppliers', orgId, filters?.criticality, filters?.category],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('suppliers')
        .select('*, owner:profiles!fk_suppliers_owner(name, email)')
        .eq('org_id', orgId);

      if (filters?.criticality && filters.criticality !== 'all') {
        query = query.eq('criticality', filters.criticality);
      }

      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }

      const { data, error } = await query.order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  const createSupplierMutation = useMutation({
    mutationFn: async (data: CreateSupplierData & { org_id: string }) => {
      const { data: result, error } = await supabase
        .from('suppliers')
        .insert([{
          name: data.name,
          category: data.category,
          criticality: data.criticality || 'medium',
          notes: data.notes,
          last_assessment: data.last_assessment,
          next_review: data.next_review,
          questionnaire_score: data.questionnaire_score,
          issues_count: data.issues_count || 0,
          owner_id: data.owner_id ?? null,
          org_id: data.org_id,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier created successfully');
    },
    onError: (error) => {
      console.error('Error creating supplier:', error);
      toast.error('Failed to create supplier');
    },
  });

  const updateSupplierMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateSupplierData }) => {
      const updateData = { ...data };
      if (updateData.owner_id === undefined) {
        // Don't update owner_id if not provided
      } else if (updateData.owner_id === '') {
        updateData.owner_id = null;
      }
      
      const { error } = await supabase
        .from('suppliers')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier updated successfully');
    },
    onError: (error) => {
      console.error('Error updating supplier:', error);
      toast.error('Failed to update supplier');
    },
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Delete access relations
      await supabase.from('access_relations').delete().eq('supplier_id', id);
      
      // Then delete the supplier
      const { error } = await supabase.from('suppliers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['access_relations'] });
      toast.success('Supplier deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting supplier:', error);
      toast.error('Failed to delete supplier');
    },
  });

  return {
    suppliers: suppliersQuery.data,
    isLoading: suppliersQuery.isLoading,
    error: suppliersQuery.error,
    createSupplier: createSupplierMutation.mutate,
    updateSupplier: updateSupplierMutation.mutate,
    deleteSupplier: deleteSupplierMutation.mutate,
    isCreating: createSupplierMutation.isPending,
    isUpdating: updateSupplierMutation.isPending,
    isDeleting: deleteSupplierMutation.isPending,
  };
};
